/* Viney interaction rules (T672 + T679): the few behaviours CSS cannot do.
   Loaded on every page from layout/theme.liquid. Everything here reads the
   server-rendered markup and survives Horizon's morph (it never inserts
   elements Horizon would remove). Session 1 (2026-10-09).
   1. Press on iPhone: Safari only applies :active once a touch listener exists.
   2. Quantity: the number rolls up on +, down on -.
   3. The price on the product page add button follows quantity, through
      variant changes and sold-out variants.
   4. The option label value ("Litur: 05 Jujube") fades when it changes.
   5. The field primitive (rule 9): check on leaving a field, then live once
      it was wrong; on submit check all and focus the first wrong field.
   Polish P1 (2026-10-29): 9b. the "Kláraðu rútínuna" pill after the first add on a product page.
   Session 2 (2026-10-09):
   6. Speed: a shade or size click shows its photo at once, warmed on hover / press.
   7. Loading (rule 8): window.vnLoader for filters, sort and load more (and inside the phone filter drawer).
   8. Card photos fade in once loaded. */
(() => {
  if (window.__vnMotion) return;
  window.__vnMotion = true;

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Session 6 (spec round 2 check 8, "nothing moves anywhere"): an autoplaying video (the home hero, Horizon's media
  // sections) does not start under reduced motion, and anything playing stops when the setting is switched on; what
  // this paused is marked data-vn-ap and plays again when the setting goes off. A video the visitor paused is never
  // resumed by this, and one they press play on is theirs (assets/media.js clears the mark). Horizon's own boxes are
  // paused and resumed one video at a time, with the box's own state and play/pause control kept true.
  // A play or pause this did not cause is the visitor's: the video is theirs from then on (its mark goes), so the
  // setting going off never restarts something they paused. own: the state change this asked for and has not seen yet.
  const own = new WeakMap();
  const theirs = (state) => (e) => {
    const v = e.target;
    if (!(v instanceof HTMLVideoElement)) return;
    if (own.get(v) === state) own.delete(v);
    else v.removeAttribute('data-vn-ap');
  };
  document.addEventListener('play', theirs('play'), true);
  document.addEventListener('pause', theirs('pause'), true);
  const stillVideos = () => {
    const red = reduced();
    for (const v of document.querySelectorAll('video')) {
      const box = v.closest('deferred-media');
      const loaded = box && box.getAttribute('data-media-loaded');
      if (red) {
        // only what is playing, or an autoplay video that has not started yet; never one the visitor paused
        const pending = v.hasAttribute('autoplay') && v.paused && v.currentTime === 0 && !loaded;
        if (v.paused && !pending) continue;
        v.setAttribute('data-vn-ap', '');
        v.removeAttribute('autoplay');
        if (!v.paused) { own.set(v, 'pause'); v.pause(); }
        // the box's state only: Horizon's pauseMedia/playMedia act on its first video, which may be another one
        if (loaded && typeof box.updatePlayPauseHint === 'function') { box.isPlaying = false; box.updatePlayPauseHint(false); }
      } else if (v.hasAttribute('data-vn-ap')) {
        v.removeAttribute('data-vn-ap');
        v.setAttribute('autoplay', '');
        if (v.paused) own.set(v, 'play');
        if (loaded && typeof box.updatePlayPauseHint === 'function') { box.isPlaying = true; box.updatePlayPauseHint(true); }
        v.play().catch(() => {});
      }
    }
  };
  stillVideos();
  // Session 6 (whole-build review): reduced motion switched on while a script animation runs (cards rising after a
  // filter, the label fade, the berry and the bag, the vine's leaves) ends it at once in its final state; the CSS ones
  // are already stopped by the reduced-motion rules. An endless one is cancelled instead.
  const settleMotion = () => {
    if (!reduced() || !document.getAnimations) return;
    for (const a of document.getAnimations()) {
      if ((window.CSSAnimation && a instanceof CSSAnimation) || (window.CSSTransition && a instanceof CSSTransition)) continue;
      try { a.finish(); } catch { a.cancel(); }
    }
  };
  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => { stillVideos(); settleMotion(); });

  // 1. Press on iPhone
  document.addEventListener('touchstart', () => {}, { passive: true });

  // Restart a one-shot CSS animation by swapping a class (u / d direction).
  const roll = (el, dir) => {
    if (!el || reduced()) return;
    el.classList.remove('vn-roll-u', 'vn-roll-d');
    void el.offsetWidth;
    el.classList.add(dir > 0 ? 'vn-roll-u' : 'vn-roll-d');
  };
  document.addEventListener('animationend', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (t.classList?.contains('vn-roll-u') || t.classList?.contains('vn-roll-d')) t.classList.remove('vn-roll-u', 'vn-roll-d');
    if (t.classList?.contains('vn-fade')) t.classList.remove('vn-fade');
  });

  // 3. Money: format a total the way the store formats the unit price, so it
  //    follows the store's money format (the server renders data-vn-money).
  const formatLike = (sample, cents) => {
    const m = String(sample).match(/\d[\d.,\s]*\d|\d/);
    if (!m) return sample;
    const num = m[0];
    const sep = (num.match(/[.,\s](?=\d{3}(\D|$))/) || [])[0] || '.';
    const dec = /[.,]\d{2}$/.test(num) && !/[.,]\d{3}$/.test(num) ? num.slice(-3, -2) : null;
    const whole = Math.floor(cents / 100);
    let out = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    if (dec) out += dec + String(cents % 100).padStart(2, '0');
    return sample.slice(0, m.index) + out + sample.slice(m.index + num.length);
  };

  const qtyOf = (form) => {
    const input = form?.querySelector('quantity-selector-component input[name="quantity"], .quantity-selector input[name="quantity"]');
    const n = parseInt(input?.value, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  const updatePrice = (priceEl, dir = 0) => {
    const unit = parseInt(priceEl.dataset.vnUnit, 10);
    const sample = priceEl.dataset.vnMoney;
    if (!Number.isFinite(unit) || !sample) return;
    const form = priceEl.closest('product-form-component');
    const text = formatLike(sample, unit * qtyOf(form));
    let inner = priceEl.firstElementChild;
    if (!inner) {
      inner = document.createElement('span');
      priceEl.textContent = '';
      priceEl.appendChild(inner);
    }
    if (inner.textContent !== text) {
      inner.textContent = text;
      if (dir) roll(inner, dir);
    }
  };
  // only the product page's add button shows a price (.vn-atc, blocks/buy-buttons.liquid); the card
  // quick-add buttons carry the same markup but no price, so they are left alone (session 2, speed:
  // re-pricing all 48 card buttons on every filter swap cost about 250 ms on a phone)
  const updateAllPrices = (root = document) =>
    root.querySelectorAll('.vn-atc .vn-atc__price[data-vn-unit]').forEach((el) => updatePrice(el));

  // 2 + 3. Quantity changes (Horizon dispatches quantity-selector:update; typing fires input/change).
  const lastQty = new WeakMap();
  const onQty = (input, animate = true) => {
    if (!input) return;
    const prev = lastQty.has(input) ? lastQty.get(input) : parseInt(input.defaultValue, 10) || 1;
    const now = parseInt(input.value, 10);
    if (!Number.isFinite(now)) return;
    lastQty.set(input, now);
    const dir = animate ? Math.sign(now - prev) : 0;
    if (dir) roll(input, dir);
    const form = input.closest('product-form-component');
    form?.querySelectorAll('.vn-atc__price[data-vn-unit]').forEach((el) => updatePrice(el, dir));
  };
  document.addEventListener('quantity-selector:update', (e) => {
    const input = /** @type {HTMLElement} */ (e.target).querySelector?.('input[name="quantity"]');
    onQty(input);
  });
  document.addEventListener('change', (e) => {
    const t = /** @type {HTMLInputElement} */ (e.target);
    if (t.matches?.('.quantity-selector input[name="quantity"]')) onQty(t);
  });
  // Typing a number: the price follows at once (no roll while typing; Horizon
  // still clamps the value to the variant's rules when the field is left).
  document.addEventListener('input', (e) => {
    const t = /** @type {HTMLInputElement} */ (e.target);
    if (t.matches?.('.quantity-selector input[name="quantity"]')) onQty(t, false);
  });

  // 3 + 4. Horizon morphs the add button and the picker from server HTML on a
  // variant change: watch for it and recompute (no loop: only text we own changes).
  const mo = new MutationObserver((records) => {
    let prices = false;
    for (const r of records) {
      const t = /** @type {Element} */ (r.target.nodeType === 1 ? r.target : r.target.parentElement);
      if (!t) continue;
      if (!prices && t.closest?.('.vn-atc')) prices = true;
      // the option label ("Litur: ...") and the filter drawer's result count (rule 4, F5)
      const val = t.closest?.('.vn-opt-value, .vn-roll-t');
      if (val && val.dataset.vnLast !== val.textContent.trim()) {
        val.dataset.vnLast = val.textContent.trim();
        // the same fade as before (vn-lab: from opacity 0, 4px down; .3s on the shared curve), played with
        // the Web Animations API: restarting a CSS animation by class needed a forced layout of the whole
        // page inside every filter swap (session 2, speed)
        if (!reduced()) val.animate?.([{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: 300, easing: 'cubic-bezier(.3, .7, .2, 1)' });
      }
    }
    if (prices) updateAllPrices();
  });
  const start = () => {
    updateAllPrices();
    document.querySelectorAll('.vn-opt-value, .vn-roll-t').forEach((v) => (v.dataset.vnLast = v.textContent.trim()));
    mo.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['data-vn-unit'] });
    initFields(document);
    animateVines(); // the vines as the page drew them: nothing moves until the cart changes
    watchOverlays();
    // quick view speed (quick-add.js): a product page tells which section holds the product, per template
    const grid = [...document.querySelectorAll('[data-product-grid-content]')].find((g) => !g.closest('dialog'));
    // Horizon's Theme is a global const (snippets/scripts.liquid), not a property of window
    const tpl = typeof Theme !== 'undefined' ? /** @type {any} */ (Theme).template?.name : undefined;
    const sec = grid?.closest('.shopify-section')?.id.replace('shopify-section-', '');
    if (sec && /^product(\.|$)/.test(tpl || '')) { try { localStorage.setItem(`vn-qv:${/** @type {any} */ (window).Shopify?.theme?.id}:${tpl}`, sec); } catch { /* storage may be blocked */ } }
  };

  // 5. The field primitive (rule 9). A form opts in with data-vn-form; each
  //    field wrapper is .vn-fw with data-vn-check (email | name | phone | text
  //    | required) and its strings in data-vn-msg-empty / data-vn-msg-bad. The
  //    form keeps its required attributes and gets novalidate, so the browser's
  //    bubble never shows but the rule still checks every field.
  const MAIL = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
  const checkField = (fw) => {
    const el = fw.querySelector('input, textarea');
    if (!el || el.disabled) return '';
    const v = (el.value || '').trim();
    const kind = fw.dataset.vnCheck || (el.type === 'email' ? 'email' : el.required ? 'required' : '');
    const optional = !el.required && fw.dataset.vnOptional !== undefined;
    const empty = fw.dataset.vnMsgEmpty || '';
    const bad = fw.dataset.vnMsgBad || empty;
    if (!v) return optional || !el.required ? '' : empty;
    switch (kind) {
      case 'email': return MAIL.test(v) ? '' : bad;
      case 'name': return v.length > 1 ? '' : bad;
      // seven digits, as the message says; +354 or 00354 in front is fine
      case 'phone': return /^(\+354|00354)?\d{7}$/.test(v.replace(/[\s-]/g, '')) ? '' : bad;
      case 'text': return v.length > 2 ? '' : bad;
      default: return '';
    }
  };
  const paint = (fw, msg) => {
    const el = fw.querySelector('input, textarea');
    const out = document.getElementById(fw.dataset.vnMsgId || (el ? `${el.id}-msg` : '')) || fw.parentElement?.querySelector('.vn-msg');
    fw.classList.toggle('is-bad', !!msg);
    fw.classList.toggle('is-good', !msg && !!(el?.value || '').trim());
    el?.setAttribute('aria-invalid', msg ? 'true' : 'false');
    if (out) {
      const cur = out.dataset.vnText || '';
      if (cur !== msg) {
        out.dataset.vnText = msg;
        out.innerHTML = '';
        if (msg) {
          const i = document.createElement('i');
          i.className = 'vn-msg__i';
          i.setAttribute('aria-hidden', 'true');
          i.textContent = '!';
          out.append(i, document.createTextNode(msg));
        }
      }
    }
  };
  const initFields = (root) => {
    root.querySelectorAll('form[data-vn-form]:not([data-vn-ready])').forEach((form) => {
      form.dataset.vnReady = '';
      form.noValidate = true;
      form.addEventListener('focusout', (e) => {
        const fw = /** @type {Element} */ (e.target).closest?.('.vn-fw');
        if (!fw) return;
        const el = fw.querySelector('input, textarea');
        // check on leaving the field; a field left empty and never touched stays quiet
        if ((el?.value || '').length || fw.dataset.vnTouched !== undefined) {
          fw.dataset.vnTouched = '';
          paint(fw, checkField(fw));
        }
      });
      form.addEventListener('input', (e) => {
        const fw = /** @type {Element} */ (e.target).closest?.('.vn-fw');
        // not while typing; once a field has been marked, re-check on every keystroke
        if (fw && fw.dataset.vnTouched !== undefined) paint(fw, checkField(fw));
        form.querySelector('.vn-srv')?.setAttribute('hidden', '');
      });
      form.addEventListener('submit', (e) => {
        let first = null;
        form.querySelectorAll('.vn-fw').forEach((fw) => {
          fw.dataset.vnTouched = '';
          const msg = checkField(fw);
          paint(fw, msg);
          if (msg && !first) first = fw.querySelector('input, textarea');
        });
        // a required tick box (e.g. the Vani consent) stays enforced
        form.querySelectorAll('.vn-cbx > input[required]').forEach((cb) => {
          if (!cb.checked && !first) first = cb;
        });
        if (first) {
          e.preventDefault();
          e.stopImmediatePropagation();
          first.focus();
        }
      }, true);
    });
  };
  window.vnFields = { init: initFields, check: checkField, paint };

  // 6. Speed (Viney/AGENTS.md rule 10, session 2): a shade or size click shows its
  //    photo at once. The server puts the picked variant's photo first in the gallery,
  //    so the first photo is pointed at it straight away (same sizes, same widths, so
  //    the gallery Horizon swaps in a moment later comes from the cache), and the photo
  //    is warmed on hover, keyboard focus or press. Inputs carry data-vn-media
  //    (snippets/variant-main-picker.liquid).
  const MEDIA = /\/cdn\/shop\/(?:files|products)\/[^?\s,]+\?v=\d+/;
  // data-vn-media holds only the media id (page weight); the gallery holds every photo of the product.
  // The first photo is re-pointed below, so its own address is kept in data-vn-src first and every
  // lookup reads that (picking B, then A again before the server answers, must show A).
  const ownSrc = (img) => img.dataset.vnSrc ?? img.getAttribute('src') ?? '';
  const ownSrcset = (img) => img.dataset.vnSrcset ?? img.getAttribute('srcset') ?? '';
  const baseOf = (input) => {
    const id = input?.dataset?.vnMedia;
    const img = id && input.closest('.shopify-section, dialog')?.querySelector(`media-gallery [data-media-id="${id}"] img`);
    return img ? ownSrc(img).match(MEDIA)?.[0] : undefined;
  };
  const firstPhoto = (input) =>
    input.closest('.shopify-section, dialog')?.querySelector('media-gallery slideshow-slide img, media-gallery .product-media img');
  const pointAt = (img, base) => ({
    srcset: ownSrcset(img).replace(new RegExp(MEDIA.source, 'g'), base),
    src: ownSrc(img).replace(MEDIA, base),
  });
  const warmed = new Set();
  const warm = (input) => {
    const base = baseOf(input);
    const img = base && firstPhoto(input);
    if (!img || warmed.has(base)) return;
    warmed.add(base);
    const next = pointAt(img, base);
    const pre = new Image();
    pre.decoding = 'async';
    pre.sizes = img.sizes;
    if (next.srcset) pre.srcset = next.srcset;
    pre.src = next.src;
  };
  const pickerInput = (t) => /** @type {Element} */ (t)?.closest?.('variant-picker label, variant-picker .variant-option__button-label')?.querySelector('input[type="radio"]');
  // the touched shade and its two visible neighbours (shoppers step through shades side by side)
  const warmNear = (input) => {
    warm(input);
    const all = [...(input.closest('fieldset')?.querySelectorAll('input[type="radio"]') || [])].filter((i) => i.closest('label')?.getBoundingClientRect().width);
    const at = all.indexOf(input);
    if (at > -1) [all[at - 1], all[at + 1]].forEach((i) => i && warm(i));
  };
  ['pointerover', 'pointerdown', 'focusin'].forEach((type) =>
    document.addEventListener(type, (e) => { const i = pickerInput(e.target); if (i) warmNear(i); }, { passive: true })
  );
  document.addEventListener('change', (e) => {
    const input = /** @type {HTMLInputElement} */ (e.target);
    if (!input.matches?.('variant-picker input[type="radio"]') || input.closest('product-card')) return;
    const base = baseOf(input);
    const img = base && firstPhoto(input);
    if (!img || (img.getAttribute('src') || '').includes(base)) return;
    // back to the first photo, then show the picked one there
    /** @type {any} */ (img.closest('media-gallery'))?.slideshow?.select?.(0, undefined, { animate: false });
    if (img.dataset.vnSrc === undefined) {
      img.dataset.vnSrc = img.getAttribute('src') || '';
      img.dataset.vnSrcset = img.getAttribute('srcset') || '';
    }
    const next = pointAt(img, base);
    if (next.srcset) img.srcset = next.srcset;
    img.src = next.src;
  });

  // 7. Loading (rule 8, board R2-8Z, session 2). facets.js and paginated-list.js call
  //    window.vnLoader(sectionId, 'top' | 'more') around their requests; the markup is
  //    snippets/vn-loader.liquid (morph leaves it alone). Timings are the board's:
  //    nothing under 300 ms (the old results only dim, at once, for filters); from 300 ms
  //    the stem grows toward 92% (92 * (1 - e^(-t/1500)), every 100 ms) with card shapes;
  //    2.5 s a quiet line; the give-up comes from the 10 s deadline in section-renderer.js.
  //    Results in: the stem to 100%, the berry, cards rise 60 ms apart, the stem fades.
  const strings = () => {
    try { return JSON.parse(document.getElementById('vn-strings')?.textContent || '{}'); } catch { return {}; }
  };
  const active = new WeakMap();
  window.vnLoader = (sectionId, where) => {
    const section = document.getElementById(sectionId.startsWith('shopify-section-') ? sectionId : `shopify-section-${sectionId}`);
    const root = /** @type {HTMLElement | null} */ (section?.querySelector(`[data-vn-ld="${where}"]`));
    if (!section || !root) return null;
    // a new wait on the same spot takes over the one before: it keeps the counts that one saved
    // and, if the stem was already showing, carries on showing it
    const carry = active.get(root)?.handoff();
    const S = strings();
    // 'top' (under the toolbar) also dims and hides the old results and says so in the count line;
    // 'drawer' (inside the phone filter drawer) and 'sheet' (inside the Húðin þín sheet) show only the stem, the
    // line and the give-up; the Húðin þín header's count lines (data-vn-ld-count) read as loading like the grid's
    const top = where === 'top';
    const results = () => section.querySelectorAll('[ref="grid"], .main-collection-grid__empty');
    const counts = () => section.querySelectorAll('.products-count-wrapper [role="status"], [data-vn-ld-count]');
    const slow = root.querySelector('.vn-slow');
    const errb = root.querySelector('.vn-errb');
    const sk = root.querySelector('.vn-sk');
    let timers = [];
    let iv = 0;
    let phase = 'idle';
    let grownAt = 0;
    const savedCounts = carry?.savedCounts || new Map();
    const setP = (p) => root.style.setProperty('--vn-p', `${p}%`);
    const clear = () => { timers.forEach(clearTimeout); timers = []; clearInterval(iv); };
    const busy = (on) => results().forEach((g) => (on ? g.setAttribute('aria-busy', 'true') : g.removeAttribute('aria-busy')));
    const restore = () => {
      if (!top) return;
      results().forEach((g) => g.classList.remove('vn-stale', 'vn-ld-hidden'));
      counts().forEach((c) => { if (savedCounts.has(c)) c.textContent = savedCounts.get(c); });
      savedCounts.clear();
      busy(false);
    };
    const grow = root.querySelector('.vn-wv__grow');
    // only the scripted run to 100% is cancelled here; the CSS growth (vn-wv-grow) is left to its class,
    // so a wait that takes over a running one keeps the stem growing from where it stands
    const stopFinish = () => grow?.getAnimations().forEach((a) => { if (!(typeof CSSAnimation !== 'undefined' && a instanceof CSSAnimation)) a.cancel(); });
    const reset = () => {
      // a finished wait cut short by a new tap: no fade-out and no slide back, the stem goes at once
      // (its delayed fade may already be under way: a duration change does not stop it, so it is cancelled)
      if (root.classList.contains('is-done')) {
        root.style.setProperty('--vn-wv-t', '0s');
        root.querySelector('.vn-wv')?.getAnimations().forEach((a) => { if (typeof CSSTransition === 'undefined' || a instanceof CSSTransition) a.cancel(); });
      }
      grownAt = 0;
      stopFinish();
      root.classList.remove('is-go', 'is-grow', 'is-done', 'is-fade', 'is-err');
      slow.hidden = true;
      errb.hidden = true;
      if (sk) sk.hidden = true;
      setP(0);
    };
    const goLoad = (since = 0) => {
      phase = 'load';
      root.style.removeProperty('--vn-wv-t');
      root.classList.add('is-go');
      if (sk) sk.hidden = false;
      if (top) {
        results().forEach((g) => g.classList.add('vn-ld-hidden'));
        counts().forEach((c) => { if (!savedCounts.has(c)) savedCounts.set(c, c.textContent); c.textContent = S.loading || c.textContent; });
      }
      if (reduced()) { setP(60); return; }
      // the stem grows by a CSS animation of 92 * (1 - e^(-t/1500)) (vn-motion.css, vn-wv-grow): no
      // script work while the shopper waits; the elapsed time gives its length at the end
      grownAt = since || performance.now();
      root.classList.add('is-grow');
    };
    const grownNow = () => (grownAt ? 92 * (1 - Math.exp(-(performance.now() - grownAt) / 1500)) : 0);
    const ctl = {
      root,
      start() {
        clear();
        reset();
        phase = 'pending';
        if (top) { results().forEach((g) => g.classList.add('vn-stale')); busy(true); }
        // taking over a wait whose stem shows: same start time, so the CSS growth carries on unbroken
        // (is-grow comes off and back on within this task, which leaves a running animation alone)
        if (carry?.wasLoad) goLoad(carry.grownAt);
        else timers.push(setTimeout(() => { if (phase === 'pending') goLoad(); }, 300));
        timers.push(setTimeout(() => {
          if (phase !== 'load') return;
          slow.textContent = S.slow || '';
          slow.hidden = false;
        }, 2500));
      },
      // the answer is in: under 300 ms nothing will show, even if the swap itself waits a frame
      arrived() {
        if (phase === 'pending') { timers.forEach(clearTimeout); timers = []; }
      },
      done(nodes) {
        const was = phase;
        clear();
        phase = 'idle';
        if (top) busy(false);
        savedCounts.clear(); // the swap brought the new count line
        if (sk) sk.hidden = true;
        slow.hidden = true;
        markLoaded(section);
        if (was !== 'load') { reset(); return; }
        // only real results land the tip leaf; the stem runs to 100% (.35s) from where it is, then fades after .9 s.
        // One style batch and no forced layout (every style change on these pages restyles about 2,000
        // elements, 200 to 400 ms on a phone); the run to 100% and the cards' rise are played with the Web
        // Animations API, so they start every time and leave nothing behind for a later wait to trip on.
        const from = reduced() ? 60 : grownNow();
        root.classList.remove('is-grow');
        setP(100);
        root.classList.add('is-done', 'is-fade');
        if (!reduced()) {
          grow?.animate([{ clipPath: `inset(-4px ${100 - from}% -4px 0)` }, { clipPath: 'inset(-4px 0% -4px 0)' }], { duration: 350, easing: 'cubic-bezier(.3, .7, .2, 1)' });
          const cards = nodes ? [...nodes] : top ? [...section.querySelectorAll('[ref="grid"] > [ref="cards[]"]')] : [];
          // cards rise 60 ms apart; backwards fill only, so nothing holds a card once it has landed
          cards.forEach((c, i) => c.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 400, delay: i * 60, easing: 'cubic-bezier(.3, .7, .2, 1)', fill: 'backwards' }));
        }
      },
      fail(retry) {
        if (grownAt) { setP(grownNow()); root.classList.remove('is-grow'); }
        clear();
        phase = 'err';
        restore();
        if (sk) sk.hidden = true;
        slow.hidden = true;
        root.classList.add('is-go', 'is-err');
        errb.querySelector('.vn-errb__t > span').textContent = (where === 'more' ? S.failedMore : S.failed) || '';
        const btn = /** @type {HTMLButtonElement} */ (errb.querySelector('.vn-errb__retry'));
        btn.textContent = S.retry || '';
        btn.onclick = () => { reset(); phase = 'idle'; retry?.(); };
        errb.hidden = false;
      },
      cancel() { clear(); phase = 'idle'; restore(); reset(); },
      handoff() { const wasLoad = phase === 'load'; const since = grownAt; clear(); phase = 'idle'; return { savedCounts, wasLoad, grownAt: since }; },
    };
    active.set(root, ctl);
    return ctl;
  };

  // 8. Single photos on cards hold their shape, then fade in .3s once loaded (rule 8).
  //    Gated on html.vn-js, so a page without this script never hides a photo.
  //    The mark is an attribute (data-vn-in) on the photo's box (.product-media) that Horizon's morph
  //    keeps (keep-list in assets/morph.js),
  //    so a photo a filter keeps stays visible; markLoaded also runs after every swap as a backstop.
  document.documentElement.classList.add('vn-js');
  const shown = (img) => (img.closest('.product-media') || img).setAttribute('data-vn-in', '1');
  // load and error do not bubble, so listen in the capture phase; a photo that broke is shown too
  ['load', 'error'].forEach((type) => document.addEventListener(type, (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (t.tagName === 'IMG' && t.closest?.('product-card')) shown(t);
  }, true));
  function markLoaded(root = document) {
    root.querySelectorAll('product-card .product-media:not([data-vn-in]) img').forEach((img) => { if (/** @type {HTMLImageElement} */ (img).complete) shown(img); });
  }
  markLoaded();

  // 9. Add to cart (rule 3; boards T3D-stay, T3D-drawer, R2-7Z quick view; F1, F4, F8; session 3).
  //    product-form.js sends vn:atc-start when an add goes out and always answers with a cart:update
  //    from the form (didError on a refusal or a network failure, with the cart read again). The pressed
  //    button (the sticky bar's when it pressed the main one) waits: a cream line draws along its foot
  //    for .55 s and holds full until the answer (F4). Success: the label rolls up, "Komið í körfu" with
  //    a drawn check rolls in, back after 1.8 s; the berry arcs to the bag (Horizon's fly-to-cart element
  //    carries it), the bag's leaf sprouts and the count bumps as it lands. In the quick view the sheet
  //    closes .7 s after the answer shows, then the berry flies. Failure: "Villa við að setja í körfu"
  //    with a "!" and no vine, back after 2.4 s. The cart never opens by itself (auto-open is off).
  const ATC = { wait: 550, ok: 1800, err: 2400, qv: 700, lost: 20000 };
  let qvOpenCount = 0; // counts quick view openings (section 11), so a timer knows its own
  window.vnAtcTiming = ATC; // read by tests/s3-checks.mjs (it shortens "lost" to test a late answer)
  const pendingAdds = new WeakMap();
  const visible = (el) => !!el && el.getClientRects().length > 0;
  const bagOf = () => [...document.querySelectorAll('.header-actions__cart-icon')].find(visible);
  const stickyFor = (form) => document.querySelector(`sticky-add-to-cart[data-product-id="${form.dataset.productId}"] [ref="addToCartButton"]`);
  const formOfSticky = (btn) => {
    const id = btn.closest('sticky-add-to-cart')?.dataset.productId;
    return id ? document.querySelector(`.shopify-section product-form-component[data-product-id="${id}"]:not(product-card product-form-component)`) : null;
  };
  // F1: on a phone the header hides while scrolling down; show it for about 1.5 s so the berry lands in the bag
  const showHeader = () => {
    const h = /** @type {HTMLElement | null} */ (document.querySelector('#header-component'));
    if (!h || (h.getBoundingClientRect().bottom > 0 && h.dataset.stickyState !== 'idle')) return;
    const before = h.dataset.stickyState;
    h.dataset.stickyState = 'active';
    let scrolled = false;
    const onScroll = () => (scrolled = true);
    window.addEventListener('scroll', onScroll, { passive: true, once: true });
    setTimeout(() => {
      window.removeEventListener('scroll', onScroll);
      // a shopper who scrolled meanwhile hands the header back to Horizon's own scroll rule
      if (scrolled || h.dataset.stickyState !== 'active') return;
      if (before === undefined) delete h.dataset.stickyState;
      else h.dataset.stickyState = before;
    }, 1500);
  };
  // the berry: Horizon's <fly-to-cart> measures both ends and arcs between them (x eases in, y eases
  // out, .62 s; the look is vn-motion.css section 17); a fixed stand-in marks where the button was,
  // because the quick view's button is gone by the time the berry leaves
  const flyBerry = (rect) => {
    if (reduced() || !customElements.get('fly-to-cart')) return;
    showHeader();
    const bag = bagOf();
    if (!bag) return;
    const src = document.createElement('span');
    src.className = 'vn-berry-src';
    src.style.cssText = `left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px`;
    document.body.append(src);
    const f = /** @type {any} */ (document.createElement('fly-to-cart'));
    f.className = 'vn-berry';
    f.source = src;
    f.destination = bag;
    document.body.append(f);
    setTimeout(() => src.remove(), 1500);
    return f;
  };
  // the bag's count (event run P1): from an add's answer it is held small, so the new count never shows full size
  // early, and each landing plays a bump of its own: the bag's leaf sprouts (.5 s) and .02 s later the count bumps
  // (.45 s). A landing ends the hold (one count for every add: two adds landing apart bump twice, a newer answer holds
  // it again). Drawn with the Web Animations API (the answer morphs the whole header section, which would strip a
  // class) on the bag shown at the time, and drawn again on the new one whenever a re-render replaces it. Nothing
  // fills once it has played (the bag leaf's hover tilt works again). Times are on the page's animation clock, the one
  // the berry's flight runs on (Gecko and WebKit, and a busy frame anywhere, put it behind performance.now()).
  const SPRING = 'cubic-bezier(.3, 1.5, .5, 1)';
  const FLY = 620; // the berry's flight (.62 s, vn-motion.css section 17)
  const clock = () => Number(document.timeline?.currentTime ?? performance.now());
  const count = { held: false, popAt: -1e4, bubble: /** @type {Element | null} */ (null), anims: /** @type {Animation[]} */ ([]), watch: /** @type {MutationObserver | null} */ (null) };
  const drawCount = () => {
    // a bump that has played is left alone (it shows nothing now); the hold, which fills, always goes
    count.anims.forEach((a) => (a.playState !== 'finished' || a.effect?.getTiming().fill === 'forwards') && a.cancel());
    count.anims = [];
    const since = clock() - count.popAt;
    const pop = since < 500;
    const on = !reduced() && (count.held || pop);
    const bag = on ? bagOf() : null;
    count.bubble = bag?.querySelector('.cart-bubble') || null;
    const play = (el, keys, opts) => {
      try {
        const a = el?.animate(keys, { easing: SPRING, ...opts });
        if (a) {
          if (opts.fill !== 'forwards') a.startTime = count.popAt;
          count.anims.push(a);
        }
      } catch { /* a browser without pseudo-element animations shows the leaf without its sprout */ }
    };
    if (bag && pop) play(bag.closest('.header-actions__action'), [{ transform: 'rotate(-40deg) scale(.4)' }, { transform: 'rotate(-40deg) scale(1.3)', offset: .6 }, { transform: 'rotate(-40deg) scale(1)' }], { duration: 500, pseudoElement: '::after' });
    if (bag) play(count.bubble, [{ transform: 'scale(.4)' }, { transform: count.held ? 'scale(.4)' : 'scale(1)' }], count.held ? { duration: 1, fill: 'forwards' } : { duration: 450, delay: 20, fill: 'backwards' });
    if (!on) {
      count.watch?.disconnect();
      count.watch = null;
    } else if (!count.watch) {
      count.watch = new MutationObserver(() => { if (!count.bubble?.isConnected) drawCount(); });
      count.watch.observe(document.body, { childList: true, subtree: true });
    }
  };
  const bump = (at) => {
    count.held = false;
    count.popAt = at - 20; // the leaf 20 ms ahead of the count
    drawCount();
    setTimeout(() => { if (!count.held && clock() - count.popAt >= 500) drawCount(); }, 520);
  };
  // an add's landing, ms from now (the count held small until then unless hold is false); the returned move(ms) moves
  // it while it is still to come and says whether it did
  const landing = (ms, hold = true) => {
    if (hold) {
      count.held = true;
      drawCount();
    }
    let t = 0;
    let done = false;
    const move = (m) => {
      if (done) return false;
      clearTimeout(t);
      const at = clock() + m;
      t = setTimeout(() => { done = true; bump(at); }, Math.max(0, m - 20));
      return true;
    };
    move(ms);
    return move;
  };
  // the landing moved onto the berry's own flight once it has started (fly-to-cart measures both ends first), read
  // once its start is set (WebKit leaves it pending at animationstart on a busy page and sets it a frame later)
  const onFlight = (f, move) => {
    if (!f || !move) return;
    const go = (e) => {
      if (e.target !== f || e.animationName !== 'vn-berry-y') return;
      f.removeEventListener('animationstart', go);
      const a = f.getAnimations().find((x) => x.animationName === 'vn-berry-y');
      a?.ready.then(() => { if (a.startTime !== null) move(Number(a.startTime) + Number(a.effect?.getComputedTiming().endTime) - clock()); }, () => {});
    };
    f.addEventListener('animationstart', go);
  };
  const settleAdd = (form, ok, seq) => {
    const p = pendingAdds.get(form);
    // an answer for another add (one the button already gave up on) settles nothing
    if (!p || p.answered || (seq !== undefined && p.seq !== undefined && seq !== p.seq)) return;
    p.answered = true;
    clearTimeout(p.lost);
    // the sticky bar pressed the main button as its puppet; Horizon clears the flag on a cart event, which a
    // given-up add never sends, so it is cleared here (else the next main-button press shows on the sticky bar)
    if (p.main) p.main.dataset.puppet = 'false';
    const wait = reduced() ? 0 : Math.max(0, ATC.wait - (performance.now() - p.t0));
    // the count lands with the berry; in the quick view at rule 3's 1.8 s at the latest (its berry moves it)
    const land = ok && !reduced() ? landing(wait + (p.inQv ? ATC.ok : FLY)) : null;
    // every delayed step checks that this add is still the form's add (a quick view reopened on the same
    // form can start a newer one meanwhile, and its state is not this add's to change)
    const mine = () => pendingAdds.get(form) === p;
    setTimeout(() => {
      if (!mine()) {
        if (p.inQv) land?.(0); // a newer add took over: no berry, the count shows now
        return;
      }
      const { btn } = p;
      btn.setAttribute('data-vn-atc', ok ? 'added' : 'err');
      btn.removeAttribute('aria-busy');
      if (ok) {
        const rect = btn.getBoundingClientRect();
        if (p.inQv) {
          // "the sheet closes, then the berry flies" (event run P1): once the close has finished, so the bag is
          // measured after the scroll restore (Firefox measured it mid-close at y -397); once, and only with the sheet
          // shut. A close that finishes after the 1.8 s (the count has shown by then) still sends its berry, which
          // lands with a bump of its own.
          let flown = false;
          const fly = () => {
            if (flown || (/** @type {HTMLDialogElement | null} */ (OVERLAYS.qv.panel()))?.open) return;
            flown = true;
            const f = flyBerry(rect);
            if (land) onFlight(f, land(FLY) ? land : landing(FLY, false));
          };
          // only the quick view the add was made in closes (closed by hand or another opened meanwhile, or a close
          // that leaves the sheet open: no berry, and the count shows now)
          setTimeout(() => {
            if (p.qvGen !== qvOpenCount || !isOpen('qv')) return void land?.(0);
            const closing = /** @type {any} */ (document.getElementById('quick-add-dialog'))?.closeDialog?.();
            Promise.resolve(closing).then(fly, fly).then(() => { if (!flown) land?.(0); });
          }, ATC.qv);
        } else onFlight(flyBerry(rect), land);
      }
      setTimeout(() => {
        if (!mine()) return;
        btn.removeAttribute('data-vn-atc');
        delete form.dataset.vnBusy;
        pendingAdds.delete(form);
      }, ok ? ATC.ok : ATC.err);
    }, wait);
  };
  document.addEventListener('vn:atc-start', (e) => {
    const form = /** @type {HTMLElement} */ (e.target);
    const main = form.querySelector('[ref="addToCartButton"]');
    const puppet = main?.dataset.puppet === 'true';
    const btn = puppet ? stickyFor(form) || main : main;
    if (!btn) return;
    btn.setAttribute('data-vn-atc', 'load');
    btn.setAttribute('aria-busy', 'true');
    form.dataset.vnBusy = '';
    const seq = /** @type {any} */ (e).detail?.seq;
    // the quick view opening this add belongs to (its .7 s close is for that one only)
    const p = { btn, seq, main: puppet ? main : null, t0: performance.now(), inQv: !!form.closest('#quick-add-dialog'), qvGen: qvOpenCount, answered: false, lost: 0 };
    // an answer that never comes (it always should) frees the button as a failure; if it comes later
    // after all, its number no longer matches and it settles nothing (the bag still shows the real cart)
    p.lost = setTimeout(() => settleAdd(form, false, seq), ATC.lost);
    pendingAdds.set(form, p);
  });
  document.addEventListener('cart:update', (e) => {
    const form = /** @type {HTMLElement} */ (e.target);
    if (form?.tagName !== 'PRODUCT-FORM-COMPONENT') return;
    const d = /** @type {any} */ (e).detail?.data;
    settleAdd(form, !d?.didError, d?.vnSeq);
  });
  // nothing is added twice while a button is waiting or answering (the board ignores presses until idle);
  // a lost button frees the lock once its add answered or its quick view reopened (a re-render mid-add keeps it)
  const freeOrphan = (form) => {
    const p = pendingAdds.get(form);
    if (p && !p.btn.isConnected && (p.answered || (p.inQv && p.qvGen !== qvOpenCount))) delete form.dataset.vnBusy;
  };
  document.addEventListener('vn:atc-busy', (e) => freeOrphan(/** @type {HTMLElement} */ (e.target)));
  document.addEventListener('click', (e) => {
    const b = /** @type {Element} */ (e.target).closest?.('[ref="addToCartButton"], .sticky-add-to-cart__button');
    if (!b) return;
    const form = /** @type {HTMLElement | null} */ (b.closest('product-form-component') || (b.closest('sticky-add-to-cart') ? formOfSticky(b) : null));
    if (form && form.dataset.vnBusy !== undefined) freeOrphan(form);
    if (form && form.dataset.vnBusy !== undefined) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);

  // 9b. "Kláraðu rútínuna" (rule 3, board T3D-routine; polish P1, 2026-10-29). After the first successful add on a
  //     product page a pill rises under the button, once per page view, with up to two products of the same brand and
  //     another type (decision L1; sections/vn-rp.liquid). Nothing of it is in the page's first HTML (rule 10): the
  //     rows are asked for when that first add goes out (`?section_id=vn-rp`), and their look and behaviour
  //     (assets/vn-rp.css, vn-rp.js, named in the answer) load only then; assets/vn-rp.js puts it in. No rows, no pill.
  //     A failed or throttled answer is asked once more (429: after its Retry-After, at most 4 s), then forgotten, so
  //     the next add asks again; a page restored from the back-forward cache with its answer still out asks again.
  //     Only the product page's own form counts (not quick view, a card's +, or the pill's own adds).
  const RP = { ask: /** @type {Promise<any> | null} */ (null), ctl: /** @type {AbortController | null} */ (null), shown: false, settled: false, want: /** @type {HTMLElement | null} */ (null), t: 0 };
  const rpForm = (/** @type {any} */ el) =>
    el?.tagName === 'PRODUCT-FORM-COMPONENT' && el.dataset.productUrl && !el.closest('dialog, product-card, vn-rp') &&
    /^product(\.|$)/.test(document.getElementById('MainContent')?.dataset.template || '') ? /** @type {HTMLElement} */ (el) : null;
  const rpLoad = (/** @type {string} */ tag, /** @type {object} */ props) => new Promise((ok, no) => {
    const el = Object.assign(document.createElement(tag), props);
    el.onload = ok;
    el.onerror = no;
    document.head.append(el);
  });
  const rpAsk = (/** @type {HTMLElement} */ form) => {
    if (RP.ask) return RP.ask;
    const ctl = (RP.ctl = new AbortController());
    RP.settled = false;
    const url = `${(form.dataset.productUrl || '').split('?')[0]}?section_id=vn-rp`;
    const later = (/** @type {number} */ ms) => new Promise((w) => setTimeout(w, ms));
    /** @returns {Promise<string>} */
    const get = (/** @type {number} */ n) => fetch(url, { signal: ctl.signal }).then((r) => {
      if (r.ok) return r.text();
      if (n || (r.status !== 429 && r.status < 500)) throw new Error(`vn-rp ${r.status}`);
      const s = Number(r.headers.get('Retry-After'));
      return later(s > 0 ? Math.min(s * 1000, 4000) : 1500).then(() => get(1));
    }, (e) => {
      if (n || ctl.signal.aborted) throw e;
      return later(1500).then(() => get(1));
    });
    const p = get(0).then((text) => {
      const host = /** @type {HTMLElement | null} */ (new DOMParser().parseFromString(text, 'text/html').querySelector('vn-rp'));
      if (!host) return { host: null };
      const d = host.dataset;
      const css = (/** @type {string | undefined} */ href) => !href || document.querySelector(`link[href="${CSS.escape(href)}"]`) ? null : rpLoad('link', { rel: 'stylesheet', href });
      const ready = Promise.all([css(d.pills), css(d.css), /** @type {any} */ (window).vnRp ? null : rpLoad('script', { src: d.js })]);
      ready.catch(() => {});
      return { host, ready };
    }).catch(() => null).then((a) => {
      if (RP.ctl === ctl) {
        RP.settled = true;
        if (!a) RP.ask = null;
      }
      return a;
    });
    RP.ask = p;
    return p;
  };
  const rpGo = async () => {
    const form = RP.want;
    if (!form || RP.shown) return;
    const ask = rpAsk(form);
    const a = await ask;
    if (!a?.host || RP.shown || RP.want !== form) return;
    try { await a.ready; } catch { if (RP.ask === ask) RP.ask = null; return; }
    const mount = /** @type {any} */ (window).vnRp;
    if (RP.shown || RP.want !== form || !form.isConnected || typeof mount !== 'function') return;
    RP.shown = !!mount(a.host, form, performance.now() - RP.t);
  };
  document.addEventListener('vn:atc-start', (e) => {
    const f = rpForm(e.target);
    if (f && !RP.shown) rpAsk(f);
  });
  document.addEventListener('cart:update', (e) => {
    const f = rpForm(e.target);
    if (!f || RP.shown || /** @type {any} */ (e).detail?.data?.didError) return;
    RP.want = f;
    RP.t = performance.now();
    rpGo();
  });
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted || RP.shown || !RP.ask || RP.settled) return;
    RP.ctl?.abort();
    RP.ask = null;
    RP.ctl = null;
    if (RP.want) rpGo();
  });

  // 10. The free-shipping vine (rule 3, snippets/free-shipping-bar.liquid): the same element on the
  //     product page, in the cart drawer and on /cart. The drawer's and /cart's are re-rendered from the
  //     server with the cart; the product page's follows the cart here. The grown stem moves .7 s, a new
  //     line's leaf pops, and at or past the threshold the tip leaf fills and one ring pulses (only when it
  //     happens, never on a page load).
  const vineState = new WeakMap();
  const VINE_LEAVES = ['--vn-l1', '--vn-l2', '--vn-l3', '--vn-l4', '--vn-l5'];
  const vineLeaves = (cart, t) => {
    let cum = 0;
    const out = [];
    (cart.items || []).forEach((item, i) => {
      cum += item.original_line_price ?? item.final_line_price ?? 0;
      const x = (cum / t) * 100;
      if (x < 98) out.push({ x, c: VINE_LEAVES[i % 5], r: i % 2 ? '215deg' : '-35deg' });
    });
    return out;
  };
  const paintVine = (el, cart) => {
    const t = parseInt(el.dataset.vnFsT, 10);
    if (!Number.isFinite(t) || typeof cart.total_price !== 'number') return;
    // Shopify's threshold counts before any discount (snippets/free-shipping-bar.liquid)
    const before = cart.original_total_price ?? cart.total_price;
    const free = before >= t;
    el.style.setProperty('--vn-fs-p', `${Math.min(100, (before / t) * 100)}%`);
    el.classList.toggle('is-free', free);
    el.querySelectorAll('[data-vn-fs-msg]').forEach((m) => (m.hidden = (m.dataset.vnFsMsg === 'free') !== free));
    const left = el.querySelector('[data-vn-fs-left]');
    if (left) left.textContent = formatLike(el.dataset.vnFsMoney || left.textContent, Math.max(0, t - before));
    const vine = el.querySelector('.vn-fs__vine');
    if (!vine) return;
    vine.querySelectorAll('.vn-fs__lf').forEach((l) => l.remove());
    const end = vine.querySelector('.vn-fs__end');
    vineLeaves(cart, t).forEach((l) => {
      const s = document.createElement('span');
      s.className = 'vn-fs__lf';
      s.style.cssText = `left:${l.x.toFixed(2)}%;--c:var(${l.c});--r:${l.r}`;
      vine.insertBefore(s, end);
    });
  };
  // after a change: pop the leaves that are new and land the tip leaf if the cart just reached free shipping
  const animateVines = () => document.querySelectorAll('.vn-fs').forEach((el) => {
    const n = el.querySelectorAll('.vn-fs__lf').length;
    const free = el.classList.contains('is-free');
    const was = vineState.get(el);
    vineState.set(el, { n, free });
    if (!was || reduced()) return;
    const leaves = [...el.querySelectorAll('.vn-fs__lf')];
    leaves.slice(was.n).forEach((l) => l.animate([{ transform: `rotate(${l.style.getPropertyValue('--r')}) scale(0)` }, { transform: `rotate(${l.style.getPropertyValue('--r')}) scale(1)` }], { duration: 450, delay: 450, easing: 'cubic-bezier(.3, 1.5, .5, 1)', fill: 'backwards' }));
    if (free && !was.free) el.querySelector('.vn-fs__end')?.classList.add('vn-land');
  });
  // a code applied or removed changes the total too (discount:update carries the cart)
  // a code applied or removed is a newer cart than every read already sent (cart-icon.js keeps the line
  // for cart:update; it does not hear discount:update)
  document.addEventListener('discount:update', (e) => {
    const w = /** @type {any} */ (window);
    const read = /** @type {any} */ (e).detail?.resource?.__vnRead;
    w.__vnCartFresh = Math.max(w.__vnCartFresh || 0, read ?? (w.__vnCartRead || 0) + 0.5);
  }, true);
  ['cart:update', 'discount:update'].forEach((type) => document.addEventListener(type, (e) => {
    const cart = /** @type {any} */ (e).detail?.resource;
    // a cart read older than the newest cart shown is skipped (window.__vnCartFresh, see cart-icon.js)
    const stale = cart?.__vnRead !== undefined && cart.__vnRead < (/** @type {any} */ (window).__vnCartFresh || 0);
    if (cart && !stale && typeof cart.total_price === 'number' && Array.isArray(cart.items)) {
      document.querySelectorAll('.vn-fs[data-vn-fs-live]').forEach((el) => paintVine(el, cart));
    }
    // the drawer and /cart are morphed by Horizon's own listeners first; look once they are done
    requestAnimationFrame(animateVines);
  }));
  document.addEventListener('animationend', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (t.classList?.contains('vn-land') && e.pseudoElement) t.classList.remove('vn-land');
  });
  // the reconciliation after a stale answer (component-cart-items.js): the product page's vine reads the cart too
  document.addEventListener('vn:cart-reconcile', async (e) => {
    const vines = document.querySelectorAll('.vn-fs[data-vn-fs-live]');
    if (!vines.length) return;
    const n = /** @type {any} */ (e).vnGen;
    try {
      const cart = await (await fetch('/cart.js')).json();
      if (n < (/** @type {any} */ (window).__vnCartFresh || 0)) return;
      vines.forEach((el) => paintVine(el, cart));
      requestAnimationFrame(animateVines);
    } catch { /* the vine stays as it was */ }
  });

  // 11. Overlays (rule 7, board R2-7Z; F2, F7; session 3). The motion is CSS (vn-motion.css section
  //     19); here: one overlay at a time, focus to the X (search: the field) and back to the opener,
  //     close by dragging (30% of the panel or a quick flick; menu left, cart right, the quick view sheet
  //     down by its handle or top; search has no drag) and by the phone's Back (a history step on open;
  //     Horizon already does this for the cart), aria-expanded on the openers. Close by X, dim and Esc
  //     are Horizon's own.
  const phone = () => window.matchMedia('(max-width: 749px)').matches;
  const OVERLAYS = {
    menu: { panel: () => document.querySelector('header-drawer .menu-drawer'), dir: 'left' },
    cart: { panel: () => document.querySelector('cart-drawer-component .cart-drawer__dialog'), dir: 'right' },
    qv: { panel: () => document.querySelector('#quick-add-dialog .quick-add-modal'), dir: 'down' },
    search: { panel: () => document.querySelector('#search-modal .search-modal__content'), dir: null },
    // VINEY (Húðin þín, S5a): the collection header's sheet (snippets/vn-skin-header.liquid), down by its handle or top;
    // S5b: on the product page from 990px the same dialog is a drawer at the right (assets/vn-skin.js section 6)
    skin: { panel: () => document.getElementById('vn-skin-sheet'), get dir() { return document.getElementById('vn-skin-sheet')?.classList.contains('vn-fit__dr') && window.matchMedia('(min-width: 990px)').matches ? 'right' : 'down'; } },
  };
  const menuDetails = () => /** @type {HTMLDetailsElement | null} */ (document.getElementById('Details-menu-drawer-container'));
  const isOpen = (k) => {
    if (k === 'menu') return !!menuDetails()?.classList.contains('menu-open');
    const d = /** @type {HTMLDialogElement | null} */ (OVERLAYS[k].panel());
    return !!d?.open && !d.classList.contains('dialog-closing');
  };
  const closeOverlay = (k) => {
    if (k === 'menu') return /** @type {any} */ (document.querySelector('header-drawer'))?.close?.();
    /** @type {any} */ (OVERLAYS[k].panel()?.closest('cart-drawer-component, quick-add-dialog, dialog-component'))?.closeDialog?.();
  };
  const openers = (k) => ({
    menu: () => [],
    cart: () => document.querySelectorAll('cart-drawer-component > .header-actions__action'),
    search: () => document.querySelectorAll('.search-action [aria-haspopup="dialog"]'),
    qv: () => document.querySelectorAll('quick-add-component[stay-visible] .quick-add__button--choose'),
    skin: () => document.querySelectorAll('.vn-hud__pill'),
  })[k]();
  const lastOpener = {};
  const wasOpen = {};
  const expanded = {};
  const noBack = {};
  const ovChanged = (k) => {
    const open = isOpen(k);
    if (open === !!wasOpen[k]) return;
    wasOpen[k] = open;
    if (open) {
      if (k === 'qv') qvOpenCount += 1;
      lastOpener[k] = document.activeElement;
      // one at a time: another open overlay closes first (its history step stays: popping it now could
      // close the overlay that is opening, which may have pushed its own step already)
      Object.keys(OVERLAYS).forEach((o) => { if (o !== k && isOpen(o)) { noBack[o] = true; closeOverlay(o); } });
      expanded[k] = [...openers(k)];
      expanded[k].forEach((b) => b.setAttribute('aria-expanded', 'true'));
      // the phone's Back closes it (a history step; the cart drawer has its own)
      // (the skin sheet shows up to 989px, so its step does too)
      if ((k === 'skin' ? window.matchMedia('(max-width: 989px)').matches : phone()) && k !== 'cart' && !history.state?.vnOverlay) history.pushState({ vnOverlay: k }, '');
      // a dialog that arrived without an animation (reduced motion) holds still at once
      requestAnimationFrame(() => {
        const pn = OVERLAYS[k].panel();
        if (pn?.tagName === 'DIALOG' && !pn.getAnimations().some((a) => a.playState === 'running')) pn.classList.add('vn-ov-settled');
      });
      // focus: the menu's X (Horizon focuses the drawer itself)
      // (the menu C2's X is .vn-md__x, snippets/vn-menu.liquid, S3)
      if (k === 'menu') setTimeout(() => { if (isOpen('menu')) /** @type {HTMLElement | null} */ (document.querySelector('.menu-drawer__close-button, .vn-md__x'))?.focus(); }, 380);
    } else {
      // the openers marked when it opened (the quick view's + loses its marker as the sheet closes)
      (expanded[k] || []).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      expanded[k] = [];
      if (history.state?.vnOverlay === k && !noBack[k]) history.back();
      noBack[k] = false;
      const panel = OVERLAYS[k].panel();
      // a close in the middle of a drag (Esc with the finger still down) ends the drag and leaves no offset
      if (drag?.k === k) drag = null;
      if (panel) panel.style.transform = '';
      if (k === 'menu') /** @type {HTMLElement | null} */ (document.querySelector('.menu-drawer__backdrop'))?.style.removeProperty('opacity');
      panel?.classList.remove('vn-ov-settled', 'vn-ov-drag', 'vn-ov-snap');
      panel?.style.removeProperty('--vn-drag-t');
      panel?.style.removeProperty('--vn-dim-o');
      // focus back to what opened it (the menu is a <details>, so Horizon leaves focus on the hidden X)
      if (k === 'menu') {
        const back = lastOpener[k] instanceof HTMLElement && lastOpener[k].isConnected ? lastOpener[k] : menuDetails()?.querySelector('summary');
        setTimeout(() => /** @type {HTMLElement} */ (back)?.focus?.({ preventScroll: true }), 300);
      }
    }
  };
  // Back: the step is already gone, so the normal close (cleanup, focus, aria) runs without popping again
  window.addEventListener('popstate', () => {
    ['menu', 'search', 'qv', 'skin'].forEach((k) => { if (isOpen(k) && history.state?.vnOverlay !== k) closeOverlay(k); });
  });
  // One at a time, in order (R2-7Z states): a dialog asked to open while another overlay is open waits
  // for that one to close (.26s), then opens; scroll lock and history steps stay with one overlay at a time.
  // Opens are serialised: an overlay asked to open while another is on screen (or still on its way in)
  // waits in a one-place queue (the newest request wins), the others close, and it opens once the screen
  // is clear. The menu goes through the same queue, so history steps and scroll lock stay with one overlay.
  // still on screen: a dialog until its close has finished (scroll restored), the menu until its slide ends
  const showing = (k) => (k === 'menu' ? !!menuDetails()?.hasAttribute('open') : !!(/** @type {HTMLDialogElement | null} */ (OVERLAYS[k].panel()))?.open);
  let opening = null; // { k, until } an open asked for but not yet on screen (Horizon opens on the next frame)
  let queued = null; // { k, go }
  const busy = (k) => Object.keys(OVERLAYS).filter((o) => o !== k && (showing(o) || opening?.k === o));
  const startOpen = (k, go) => {
    opening = { k, until: performance.now() + 600 };
    go();
    const done = () => (showing(k) || performance.now() > opening?.until ? (opening = opening?.k === k ? null : opening) : requestAnimationFrame(done));
    requestAnimationFrame(done);
  };
  const requestOpen = (k, go) => {
    if (!busy(k).length && !queued) return startOpen(k, go);
    queued = { k, go };
    const t0 = performance.now();
    // the previous overlay's history step (Horizon's cart, or ours) is popped asynchronously as it closes;
    // the next one opens only once it is gone, so that pop cannot close the overlay that just opened
    const stepLeft = () => !!(history.state?.cartDrawerOpen || history.state?.vnOverlay);
    const drain = () => {
      if (!queued) return;
      const others = busy(queued.k);
      others.forEach((o) => { if (isOpen(o)) closeOverlay(o); });
      if ((others.length || stepLeft()) && performance.now() - t0 < 2000) return requestAnimationFrame(drain);
      const q = queued;
      queued = null;
      startOpen(q.k, q.go);
    };
    requestAnimationFrame(drain);
  };
  customElements.whenDefined('dialog-component').then(() => {
    const D = /** @type {any} */ (customElements.get('dialog-component'));
    const show = D?.prototype?.showDialog;
    if (!show || show.__vn) return;
    D.prototype.showDialog = function (...args) {
      const mine = Object.keys(OVERLAYS).find((k) => OVERLAYS[k].panel() === this.refs?.dialog);
      if (!mine || this.refs?.dialog?.open) return show.apply(this, args);
      requestOpen(mine, () => show.apply(this, args));
    };
    D.prototype.showDialog.__vn = true;
  });
  customElements.whenDefined('header-drawer').then(() => {
    const H = /** @type {any} */ (customElements.get('header-drawer'));
    const open = H?.prototype?.open;
    if (!open || open.__vn) return;
    H.prototype.open = function (target, event) {
      // a submenu inside the open menu opens as before; only the menu itself waits its turn
      if (isOpen('menu') || target) return open.call(this, target, event);
      // Horizon's summary has already set the <details> open by the time this runs; it stays shut until its turn
      const d = menuDetails();
      if (!busy('menu').length && !queued) return startOpen('menu', () => open.call(this, target, event));
      if (d?.open) d.open = false;
      requestOpen('menu', () => { if (d) d.open = true; open.call(this, target, event); });
    };
    H.prototype.open.__vn = true;
  });
  // Esc: Chrome closes a modal dialog itself (its close watcher), skipping Horizon's animated close; the
  // cancel is taken back so Esc closes with the same .26s out as the X and the dim
  document.addEventListener('cancel', (e) => {
    const k = ['cart', 'qv', 'search', 'skin'].find((o) => OVERLAYS[o].panel() === e.target);
    if (!k) return;
    e.preventDefault();
    closeOverlay(k);
  }, true);
  // once a dialog has arrived it holds still (no animation), so a drag can move it
  document.addEventListener('animationend', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (!e.pseudoElement && /^vn-(in-right|in-up|drop-in)$/.test(e.animationName) && t.matches?.('dialog[open]:not(.dialog-closing)')) t.classList.add('vn-ov-settled');
  });
  const watchOverlays = () => {
    const ob = new MutationObserver(() => Object.keys(OVERLAYS).forEach(ovChanged));
    const d = menuDetails();
    if (d) ob.observe(d, { attributes: true, attributeFilter: ['class', 'open'] });
    ['cart', 'qv', 'search', 'skin'].forEach((k) => { const p = OVERLAYS[k].panel(); if (p) ob.observe(p, { attributes: true, attributeFilter: ['open', 'class'] }); });
    // a panel put in by a later script (the product page's Húðin þín sheet, assets/vn-skin.js section 6)
    /** @type {any} */ (window).vnWatchOverlay = (p) => { if (p) ob.observe(p, { attributes: true, attributeFilter: ['open', 'class'] }); };
    Object.keys(OVERLAYS).forEach((k) => openers(k).forEach((b) => b.setAttribute('aria-expanded', 'false')));
    document.querySelectorAll('.quick-add__button--choose').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  };
  // drag to close: 30% of the panel or a flick (faster than .6 px per ms)
  let drag = null;
  document.addEventListener('pointerdown', (e) => {
    if (e.button > 0) return;
    const t = /** @type {Element} */ (e.target);
    const k = Object.keys(OVERLAYS).find((o) => OVERLAYS[o].dir && isOpen(o) && OVERLAYS[o].panel()?.contains(t));
    if (!k || t.closest('input, textarea, select, [contenteditable]')) return;
    const panel = /** @type {HTMLElement} */ (OVERLAYS[k].panel());
    // the sheet drags only by its handle or its top
    if (k === 'qv' && !(phone() && (t.closest('.vn-qv-handle') || e.clientY - panel.getBoundingClientRect().top < 120))) return;
    if (k === 'qv' && !phone()) return;
    if (k === 'skin' && OVERLAYS.skin.dir === 'down' && !(t.closest('.vn-hud__grab') || e.clientY - panel.getBoundingClientRect().top < 120)) return;
    drag = { k, panel, x: e.clientX, y: e.clientY, t: performance.now(), d: 0, on: false, id: e.pointerId };
  }, true);
  document.addEventListener('pointermove', (e) => {
    const g = drag;
    if (!g || e.pointerId !== g.id) return;
    const dx = e.clientX - g.x, dy = e.clientY - g.y;
    const dir = OVERLAYS[g.k].dir;
    const d = dir === 'left' ? Math.min(0, dx) : dir === 'right' ? Math.max(0, dx) : Math.max(0, dy);
    if (!g.on) {
      if (Math.abs(d) < 8) return;
      if (dir !== 'down' && Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
      if (g.panel.tagName === 'DIALOG' && !g.panel.classList.contains('vn-ov-settled')) { drag = null; return; }
      g.on = true;
      g.panel.classList.add('vn-ov-drag');
      try { g.panel.setPointerCapture(g.id); } catch { /* the pointer may already be gone */ }
    }
    g.d = d;
    const tf = dir === 'down' ? `translateY(${d}px)` : `translateX(${d}px)`;
    g.panel.style.transform = tf;
    const size = dir === 'down' ? g.panel.offsetHeight : g.panel.offsetWidth;
    const o = String(Math.max(0, 1 - Math.abs(d) / size));
    if (g.k === 'menu') /** @type {HTMLElement | null} */ (document.querySelector('.menu-drawer__backdrop'))?.style.setProperty('opacity', o);
    else g.panel.style.setProperty('--vn-dim-o', o);
  }, { passive: true });
  const endDrag = (e) => {
    const g = drag;
    if (!g || e.pointerId !== g.id) return;
    drag = null;
    if (!g.on) return;
    const dir = OVERLAYS[g.k].dir;
    const size = dir === 'down' ? g.panel.offsetHeight : g.panel.offsetWidth;
    const v = Math.abs(g.d) / Math.max(1, performance.now() - g.t);
    const close = Math.abs(g.d) > size * 0.3 || v > 0.6;
    const tf = g.panel.style.transform;
    g.panel.classList.remove('vn-ov-drag');
    /** @type {HTMLElement | null} */ (document.querySelector('.menu-drawer__backdrop'))?.style.removeProperty('opacity');
    if (close) {
      // the closing slide starts where the finger left the panel
      g.panel.style.setProperty('--vn-drag-t', tf);
      g.panel.style.transform = '';
      closeOverlay(g.k);
    } else {
      g.panel.style.removeProperty('--vn-dim-o');
      if (g.panel.tagName === 'DIALOG') {
        g.panel.classList.add('vn-ov-snap');
        g.panel.addEventListener('transitionend', () => g.panel.classList.remove('vn-ov-snap'), { once: true });
      }
      g.panel.style.transform = '';
    }
  };
  document.addEventListener('pointerup', endDrag, true);
  document.addEventListener('pointercancel', endDrag, true);

  // 12. The cart's code box (rule 9 field in rule 6, boards R2-6Z / R2-9Z; snippets/cart-summary.liquid):
  //     the ghost link opens the field and puts the cursor in it; typing again after a refused code takes
  //     the wrong mark and its line away (the code itself stays, live bug 4).
  document.addEventListener('toggle', (e) => {
    const d = /** @type {HTMLDetailsElement} */ (e.target);
    if (d.classList?.contains('vn-code__d') && d.open) /** @type {HTMLInputElement | null} */ (d.querySelector('input'))?.focus();
  }, true);
  document.addEventListener('input', (e) => {
    const t = /** @type {HTMLInputElement} */ (e.target);
    if (!t.matches?.('.vn-code__fw > input')) return;
    const fw = t.closest('.vn-fw');
    if (!fw?.classList.contains('is-bad')) return;
    fw.classList.remove('is-bad');
    t.setAttribute('aria-invalid', 'false');
    t.closest('cart-discount-component')?.querySelectorAll('.cart-discount__error, .cart-discount__error-text').forEach((el) => el.classList.add('hidden'));
  });

  // 13. Scroll arrival (rule 10, boards R2-10Z; session 4). One IntersectionObserver for [data-vn-reveal]:
  //     a block rises 8px and fades in (.5s) the first time 18% of it is in view (8% above the bottom
  //     edge), once per page view; a row (data-vn-reveal="row") lets its items (data-vn-ri) rise 60 ms
  //     apart, the stagger stopping at 180 ms; the divider vine ("vine") and the sign-off ("sprig") grow
  //     and stay. Nothing on the first screen moves, nor anything reached by a jump (an anchor, Back with
  //     restored scroll) or above it: those are simply there. Blocks are hidden only by this script (the
  //     vn-rv-h class) and only when they are below the screen, so a page without it shows everything.
  //     Reduced motion: nothing is hidden. Opacity and transform only (the vine draws its stroke).
  const reveal = (() => {
    const pending = new Set();
    const moving = new Map(); // block -> its parts while they arrive
    const watched = new Map(); // the element the observer watches -> its block
    let io = null;
    const items = (el) => (el.dataset.vnReveal === 'row' ? [...el.querySelectorAll('[data-vn-ri]')] : [el]);
    // a block that is arriving ends where it is going, at once (a jump, a back/forward-cache return)
    const finish = (el) => {
      const parts = moving.get(el);
      if (!parts) return;
      moving.delete(el);
      parts.forEach((p) => { p.classList.add('vn-rv-x'); p.classList.remove('vn-rv-a'); p.style.transitionDelay = ''; });
      void document.body.offsetWidth;
      parts.forEach((p) => p.classList.remove('vn-rv-x'));
    };
    const show = (el, animate) => {
      pending.delete(el);
      watched.forEach((b, t) => { if (b === el) { io?.unobserve(t); watched.delete(t); } });
      const parts = items(el).filter((p) => p.classList.contains('vn-rv-h'));
      if (!animate) { parts.forEach((p) => p.classList.remove('vn-rv-h')); return; }
      // the stagger counts the items that are laid out (a carousel's mobile copy is not)
      let i = 0;
      parts.forEach((p) => {
        p.classList.add('vn-rv-a');
        if (el.dataset.vnReveal === 'row' && p.getClientRects().length) p.style.transitionDelay = `${Math.min(i++, 3) * 60}ms`;
        p.classList.remove('vn-rv-h');
      });
      moving.set(el, parts);
      setTimeout(() => { if (moving.get(el) !== parts) return; moving.delete(el); parts.forEach((p) => { p.classList.remove('vn-rv-a'); p.style.transitionDelay = ''; }); }, 1700);
    };
    // everything whose top is above the given line counts as seen: shown at once, and what is arriving there ends
    const passAbove = (y) => {
      pending.forEach((el) => { if (el.getBoundingClientRect().top < y) show(el, false); });
      moving.forEach((_, el) => { if (el.getBoundingClientRect().top < y) finish(el); });
    };
    window.addEventListener('pageshow', (e) => { if (e.persisted) { [...moving.keys()].forEach(finish); passAbove(window.innerHeight); } });
    const setup = () => {
      // a home media section rises in two blocks: the big image (marked in snippets/media.liquid) and its
      // heading; the heading's text block is Horizon's shared block, so it is marked here, before anything hides
      document.querySelectorAll('.media-with-content .media-block[data-vn-reveal]').forEach((m) => {
        const h = m.closest('.media-with-content')?.querySelector('.media-with-content__content h1, .media-with-content__content h2, .media-with-content__content h3');
        (h?.closest('.text-block') || h)?.setAttribute('data-vn-reveal', '');
      });
      const all = [...document.querySelectorAll('[data-vn-reveal]')];
      if (!all.length || reduced() || typeof IntersectionObserver === 'undefined') return;
      const vh = window.innerHeight;
      const below = all.filter((el) => el.getBoundingClientRect().top >= vh);
      if (!below.length) return;
      document.documentElement.classList.add('vn-rv');
      io = new IntersectionObserver((list) => list.forEach((en) => {
        const el = watched.get(en.target);
        if (en.isIntersecting && el) show(el, true);
      }), { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
      below.forEach((el) => {
        const parts = items(el);
        parts.forEach((p) => p.classList.add('vn-rv-h'));
        pending.add(el);
        // a row is watched through its first laid-out item, so a tall row arrives when its first cards do
        const t = el.dataset.vnReveal === 'row' ? parts.find((p) => p.getClientRects().length) || el : el;
        watched.set(t, el);
        io.observe(t);
      });
    };
    // an in-page anchor: the target and everything above it are simply there, before the browser scrolls
    document.addEventListener('click', (e) => {
      if ((!pending.size && !moving.size) || e.defaultPrevented) return;
      const a = /** @type {HTMLAnchorElement | null} */ (/** @type {Element} */ (e.target).closest?.('a[href*="#"]'));
      if (!a || a.origin !== location.origin || a.pathname !== location.pathname || a.hash.length < 2) return;
      let id = a.hash.slice(1);
      try { id = decodeURIComponent(id); } catch { /* keep it as written */ }
      const t = document.getElementById(id);
      if (t) passAbove(t.getBoundingClientRect().top + window.innerHeight);
    }, true);
    // the address's anchor changed (the theme scrolls there smoothly): its target and everything above it
    const passHash = () => {
      let id = location.hash.slice(1);
      try { id = decodeURIComponent(id); } catch { /* keep it as written */ }
      const t = id ? document.getElementById(id) : null;
      passAbove((t ? t.getBoundingClientRect().top : 0) + 2 * window.innerHeight);
    };
    window.addEventListener('hashchange', () => { if (pending.size || moving.size) passHash(); });
    const nav = /** @type {PerformanceNavigationTiming | undefined} */ (performance.getEntriesByType?.('navigation')[0]);
    // a jump on arrival (an anchor in the address, or Back with its scroll restored): wait until the
    // browser has scrolled, so nothing is hidden that the jump lands on. Everything above the anchor's
    // target (and a screen below it) counts as passed. The browser may scroll again as photos load and
    // the page grows; until the visitor first scrolls, taps or types, such a scroll is a jump too (the one
    // scroll listener here, gone at the first input).
    const jumpLoad = location.hash.length > 1 || nav?.type === 'back_forward';
    const afterJump = () => { if (location.hash.length > 1) passHash(); else passAbove(window.innerHeight); };
    const watchLateJumps = () => {
      const onScroll = () => { if (pending.size || moving.size) passAbove(window.innerHeight); };
      const stop = () => { window.removeEventListener('scroll', onScroll); ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((t) => window.removeEventListener(t, stop, true)); };
      window.addEventListener('scroll', onScroll, { passive: true });
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((t) => window.addEventListener(t, stop, { capture: true, passive: true }));
    };
    return () => {
      if (!jumpLoad) { setup(); return; }
      const go = () => requestAnimationFrame(() => requestAnimationFrame(() => { setup(); afterJump(); watchLateJumps(); }));
      if (document.readyState === 'complete') go();
      else window.addEventListener('load', go, { once: true });
    };
  })();

  // 14. Sending a form (rule 9, boards R2-9Z; session 4): newsletter, contact, Vani. Once section 5 has
  //     passed every field (it stops the event otherwise), the button draws its wait line (.7s, then holds)
  //     and a second press sends nothing. Shopify's own bot check (hCaptcha) takes over every contact and
  //     customer form: it cancels the submit event, checks the visitor, then sends the form itself, so the
  //     wait line starts on the cancelled event too, and a second press is stopped before Shopify's handler
  //     sees it. Typing in the form frees the button again (a visitor who left the check can send again).
  //     The form's id is remembered for the page that comes back, so its answer shows only in that form
  //     (snippets/vn-posted-form.liquid, live bug 3), and the Vani first name, which Shopify does not
  //     return on a refusal, is kept and put back (everything typed stays).
  const POSTED = 'vn-posted';
  const VANI_NAME = 'vn-vani-name';
  const store = (fn) => { try { return fn(sessionStorage); } catch { return null; /* storage may be blocked */ } };
  const sendBtn = (t) => (/** @type {Element} */ (t).matches?.('form[data-vn-form]') ? /** @type {Element} */ (t).querySelector('.vn-sb') : null);
  // While a send is pending the form holds still: nothing can be typed, the consent cannot be unticked and a
  // second press sends nothing, so what Shopify's check sends is what passed the rule. The send owns the form
  // until it completes (the page moves on) or the form's own challenge is closed without sending (Shopify's
  // check then gives up; the visitor can change the form and send again). A back/forward-cache return frees it.
  // Each form's challenge is found through hCaptcha's widget id: the widget frame Shopify puts inside the form
  // carries data-hcaptcha-widget-id, and its challenge frame names the same id in its address
  // (hcaptcha.html#frame=challenge&id=<widget id>; js.hcaptcha.com/1/api.js, read 2026-10-10).
  const held = new Map(); // form -> stop()
  const freeBtn = (btn) => {
    btn.removeAttribute('data-vn-wait'); btn.removeAttribute('aria-busy');
    const form = btn.closest('form'); const stop = form && held.get(form);
    if (stop) { held.delete(form); stop(); }
  };
  const hold = (form, btn) => {
    let shown = false;
    // the form's widget id is read on every check (hCaptcha may render the widget a moment after the press);
    // without it no challenge counts as this form's, so another form's challenge never frees this one
    const ofForm = (f) => {
      const wid = form.querySelector('iframe[data-hcaptcha-widget-id]')?.getAttribute('data-hcaptcha-widget-id');
      if (!wid) return false;
      try { return new URLSearchParams(new URL(f.src).hash.slice(1)).get('id') === wid; } catch { return false; }
    };
    const frames = () => [...document.querySelectorAll('iframe[src*="hcaptcha"][src*="frame=challenge"]')].filter(ofForm);
    const visible = () => frames().some((f) => { const r = f.getBoundingClientRect(); return r.width > 0 && r.height > 0 && getComputedStyle(f).visibility !== 'hidden' && getComputedStyle(f.parentElement || f).visibility !== 'hidden'; });
    const mo = new MutationObserver(() => { if (visible()) shown = true; else if (shown) freeBtn(btn); });
    mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['style'] });
    held.set(form, () => mo.disconnect());
  };
  const heldForm = (t) => { const f = /** @type {Element} */ (t).closest?.('form[data-vn-form]'); return f && held.has(f) ? f : null; };
  document.addEventListener('beforeinput', (e) => { if (heldForm(e.target)) e.preventDefault(); }, true);
  document.addEventListener('click', (e) => { const f = heldForm(e.target); if (f && /** @type {Element} */ (e.target).closest('label, input[type="checkbox"]')) e.preventDefault(); }, true);
  document.addEventListener('submit', (e) => {
    const btn = sendBtn(e.target);
    if (btn?.hasAttribute('data-vn-wait')) { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('submit', (e) => {
    const form = /** @type {HTMLFormElement} */ (e.target);
    const btn = sendBtn(form);
    if (!btn) return;
    // Shopify's wrapper marks a form it has taken over with data-hcaptcha-bound. A press it cancels before
    // that (its check still loading) starts nothing: the button stays free to press again. A bound form's
    // send is Shopify's check, so the form is held; a send not cancelled at all goes out natively.
    const bound = form.hasAttribute('data-hcaptcha-bound');
    if (e.defaultPrevented && !bound) return;
    btn.setAttribute('data-vn-wait', '');
    btn.setAttribute('aria-busy', 'true');
    if (bound) hold(form, btn);
  });
  // the last backstop at the send itself (the hold above already keeps the form still): should anything
  // still be wrong while the browser builds what is sent (a browser's autofill changing a field), the
  // address is left out and Shopify refuses the send
  document.addEventListener('formdata', (e) => {
    const form = /** @type {HTMLFormElement} */ (e.target);
    if (!form.matches?.('form[data-vn-form]') || !form.querySelector('.vn-sb')) return;
    const wrong = [...form.querySelectorAll('.vn-fw')].some((fw) => checkField(fw)) || [...form.querySelectorAll('.vn-cbx > input[required]')].some((cb) => !cb.checked);
    if (wrong) { e.formData.delete('contact[email]'); return; }
    // the form whose send the browser is building now is the one the next page answers (with two sends in
    // flight, the last press is not necessarily the one that lands)
    if (form.querySelector('.vn-sb[data-vn-wait]')) store((s) => {
      s.setItem(POSTED, form.id);
      const name = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-vn-vani-name]'));
      if (name) s.setItem(VANI_NAME, name.value);
    });
  }, true); // formdata does not bubble
  // a page shown again from the back/forward cache: the button is free again
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) document.querySelectorAll('.vn-sb[data-vn-wait]').forEach(freeBtn);
  });
  const afterPost = () => {
    const answer = /** @type {HTMLElement | null} */ (document.querySelector('form[data-vn-form] [data-vn-posted="ok"], form[data-vn-form] [data-vn-posted="err"]'));
    if (answer) {
      // a refusal: the first name comes back into the Vani form, and the answer gets focus
      const name = /** @type {HTMLInputElement | null} */ (answer.closest('form')?.querySelector('[data-vn-vani-name]') || null);
      const kept = store((s) => s.getItem(VANI_NAME));
      if (name && !name.value && kept && answer.dataset.vnPosted === 'err') name.value = kept;
      if (!answer.hasAttribute('tabindex')) answer.setAttribute('tabindex', '-1');
      answer.focus();
    }
    // forgotten on the first page without an answer, so a reload of the answer page still knows its form
    if (!document.querySelector('[data-vn-posted]')) store((s) => { s.removeItem(POSTED); s.removeItem(VANI_NAME); });
  };

  // 15. Product cards by keyboard (rule 2; speed pass, session 4): the card's sprig and ring show while its link
  //     has keyboard focus. An attribute set here (data-vn-kf: "c" for the photo and title links, which draw the
  //     ring on the card, "1" for the card link; kept through Horizon's morph by assets/morph.js) replaces
  //     product-card:has(...:focus-visible), which Chrome evaluated by searching every card's subtree on each
  //     page-wide style recalculation (vn-motion.css section 13).
  document.addEventListener('focusin', (e) => {
    const t = /** @type {Element} */ (e.target);
    const card = t.closest?.('product-card');
    if (!card || !t.matches('.product-card__link, a.contents') || !t.matches(':focus-visible')) return;
    card.setAttribute('data-vn-kf', t.matches('a.contents') ? 'c' : '1');
  });
  document.addEventListener('focusout', (e) => {
    /** @type {Element} */ (e.target).closest?.('product-card')?.removeAttribute('data-vn-kf');
  });
  // keyboard use can start on a link that already has focus (no new focusin): mark it then
  document.addEventListener('keydown', () => {
    const t = document.activeElement;
    const card = t?.closest?.('product-card');
    if (card && !card.hasAttribute('data-vn-kf') && t.matches('.product-card__link, a.contents') && t.matches(':focus-visible')) card.setAttribute('data-vn-kf', t.matches('a.contents') ? 'c' : '1');
  }, true);
  // 16. The season after a back/forward-cache return (snippets/vn-season.liquid only runs while the page is
  //     read): a page left in one season and shown again in the next takes the visitor's season
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    const m = new Date().getMonth() + 1;
    const s = m > 11 || m < 3 ? 'vetur' : m < 6 ? 'vor' : m < 9 ? 'sumar' : 'haust';
    document.documentElement.setAttribute('data-vn-season', s);
  });

  // 17. The vine with leaves under text links (homepage spec section 8, part H1; board S-states). A link in rich
  //     text cannot carry markup from the theme, so its vine (snippets/vn-leafvine.liquid's markup: two leaves
  //     and the tip; vn-motion.css sections 7 and 26) goes in on its first hover, press or keyboard focus, and the
  //     link is marked vn-ilv (its ink line then gives way to the stem). The vine hangs from a zero-width end mark
  //     after the last word, as wide as the last line (--vn-lw, measured again on every hover or press and on a
  //     resize; assets/morph.js measures it again after a refresh), so a
  //     link that wraps grows its vine under its last line. It goes in at rest and is let go after one style
  //     read, so it grows from nothing. assets/morph.js keeps the mark and the vine through a section refresh.
  //     A link that holds a picture keeps its ink line only. On touch a finger on a ghost or text link marks it
  //     data-vn-press until it lifts (or the page scrolls), and the vine shows while it is marked and holds .5s
  //     after (Site Patterns 9.3); Chromium shows :active on touch only at the tap, so the mark is set here.
  const TEXT_LINK = '.vn-il, :is(.rte, .text-block, .vn-pill__body, .vn-quiet) a:not([class*="button"], .vn-gh)';
  const vineLink = (e) => {
    const a = /** @type {Element} */ (e.target).closest?.('a, .vn-il');
    // the external mark (snippets/vn-ext-mark.liquid, S3) is not a picture: such a link keeps its vine
    if (!a || !a.matches(TEXT_LINK) || a.querySelector('img, picture, video, svg:not(.vn-ext)')) return;
    const rects = a.getClientRects();
    if (!rects.length) return;
    const w = Math.round(rects[rects.length - 1].width);
    const end = /** @type {HTMLElement | null} */ (a.querySelector(':scope > .vn-ilv__end'));
    if (end) { end.style.setProperty('--vn-lw', w + 'px'); return; }
    a.insertAdjacentHTML('beforeend', '<i class="vn-ilv__end" style="--vn-lw:' + w + 'px" aria-hidden="true"><i class="vn-lv vn-lv--new"><i></i><i></i><i></i></i></i>');
    a.classList.add('vn-ilv');
    const v = /** @type {HTMLElement} */ (/** @type {Element} */ (a.lastElementChild).firstElementChild);
    getComputedStyle(/** @type {Element} */ (v.firstElementChild)).transform;
    v.classList.remove('vn-lv--new');
  };
  for (const t of ['pointerover', 'pointerdown', 'focusin']) document.addEventListener(t, vineLink, { passive: true });
  // a resize can move a wrapped link's last line: every link that holds a vine takes the new width (once a frame)
  let lwFrame = 0;
  window.addEventListener('resize', () => {
    if (lwFrame) return;
    lwFrame = requestAnimationFrame(() => {
      lwFrame = 0;
      for (const end of document.querySelectorAll('.vn-ilv > .vn-ilv__end')) {
        const rects = /** @type {Element} */ (end.parentElement).getClientRects();
        if (rects.length) /** @type {HTMLElement} */ (end).style.setProperty('--vn-lw', Math.round(rects[rects.length - 1].width) + 'px');
      }
    });
  }, { passive: true });
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return;
    const a = /** @type {Element} */ (e.target).closest?.('.vn-gh, .vn-ilv');
    if (!a) return;
    a.setAttribute('data-vn-press', '');
    const up = () => {
      a.removeAttribute('data-vn-press');
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
    };
    document.addEventListener('pointerup', up, { passive: true });
    document.addEventListener('pointercancel', up, { passive: true });
  }, { passive: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { start(); reveal(); afterPost(); });
  else { start(); reveal(); afterPost(); }
})();
